import {Form, Formik} from 'formik'
import {FC, useCallback, useEffect, useState} from 'react'
import {CarStatusTable} from './CarStatusTable'
import { ICarModel, ICarStatusFilterModel, initialCarStatusFilter, validationCarStatusFilter } from './CarModel'
import { branches, vehicleGroups } from '../../../shared/models/SharedApiLookups'
import { showErrorToast } from '../../../shared/components/ToastUtils'
import { filterCarStatus } from './Api'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import GroupSelect from '../../../shared/components/GroupSelect'
import SelectInput from '../../../shared/components/SelectInput'
import { DATE_FILTER_TYPE } from '../../../shared/models/SharedConstantLookups'
import DateInput from '../../../shared/components/DateInput'
import { ToastContainer } from 'react-toastify'

export const CarStatusListPage: FC = () => {
  const [cars, setCars] = useState<ICarModel[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{value: string; label: string}[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)
  const [filterValues, setFilterValues] = useState<ICarStatusFilterModel>(initialCarStatusFilter)
  const [filterOpen, setFilterOpen] = useState(true);

  useEffect(() => {
    Promise.all([branches(), vehicleGroups()]).then(([brn, vg]) => {
      setBranchOptions(brn)
      setVehicleGroupOptions(vg)
    })
  }, [])

  const filter = useCallback(
    async (
      values: ICarStatusFilterModel,
      page: number = 0,
      size: number = pageSize,
      sort?: string,
      order?: 'asc' | 'desc'
    ) => {
      try {
        setLoading(true)
        const res = await filterCarStatus(values, page, size, sort, order)
        const data = res.data.payload || res.data
        setCars(data?.content ?? [])
        setTotalPages(data?.totalPages ?? 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Rezervasyonlar yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        filter(filterValues, newPage, pageSize, sortBy, sortOrder)
      }
    },
    [totalPages, pageSize, sortBy, sortOrder, filterValues, filter]
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize)
      setCurrentPage(0)
      filter(filterValues, 0, newPageSize, sortBy, sortOrder)
    },
    [filterValues, sortBy, sortOrder, filter]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      filter(filterValues, currentPage, pageSize, column, order)
    },
    [currentPage, filterValues, pageSize, filter]
  )

  const handleFilter = async (values: ICarStatusFilterModel) => {
    setFilterValues(values)
    setCurrentPage(0)
    filter(values, 0, pageSize, sortBy, sortOrder)
  }

  return (
    <>
  <div className='card'>
    {/* Card Header - Filtre Bölümü */}
    <div className='card-header border-0 pt-6'>
      <div className='card-title'>
        <div className='d-flex align-items-center gap-2 my-1'>
          <i className='bi bi-funnel fs-1 text-primary'></i>
          <h3 className='fw-bold m-0'>Araç Durumu Filtreleri</h3>
        </div>
      </div>

      <div className='card-toolbar'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => setFilterOpen((prev) => !prev)}
        >
          <i className={`bi bi-chevron-${filterOpen ? 'up' : 'down'} fs-4`}></i>
          {filterOpen ? 'Gizle' : 'Göster'}
        </button>
      </div>
    </div>

    {/* Collapsible Filter Form */}
    <div className={`collapse ${filterOpen ? 'show' : ''}`}>
      <div className='card-body border-top pt-6'>
        <Formik
          initialValues={initialCarStatusFilter}
          enableReinitialize
          validationSchema={validationCarStatusFilter}
          onSubmit={(values) => handleFilter(values)}
        >
          {(formik) => (
            <Form id='carStatusFilterForm' className='form'>
              {/* Row 1 */}
              <div className='row g-6 mb-6'>
                <TextAreaInput
                  name='plateList'
                  label='Aranacak Plakalar'
                  placeholder='Plaka(lar) - Çoklu arama için plakalar arasında virgül olmalıdır!'
                  wrapperClassName='col-md-6'
                />
                <GroupSelect
                  name='vehicleGroupIdList'
                  label='Aranacak Gruplar'
                  wrapperClassName='col-md-6'
                  options={vehicleGroupOptions}
                />
              </div>

              {/* Row 2 */}
              <div className='row g-6 mb-6'>
                <SelectInput
                  name='dateFilterType'
                  label='Tarih Filtresi'
                  firstItemLabel='Tarih Önemsiz'
                  wrapperClassName='col-md-2'
                  options={DATE_FILTER_TYPE}
                />
                <DateInput
                  name='startDate'
                  type='date'
                  label='Başlangıç'
                  wrapperClassName='col-md-2'
                />
                <DateInput
                  name='endDate'
                  type='date'
                  label='Bitiş'
                  wrapperClassName='col-md-2'
                />
                 <GroupSelect
                  name='officeIdList'
                  label='Aranacak Şubeler'
                  wrapperClassName='col-md-6'
                  options={branchOptions}
                />
              </div>

              {/* Action Buttons */}
              <div className='d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light btn-active-light-primary'
                  onClick={() => formik.resetForm()}
                >
                  Temizle
                </button>

                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={loading}
                >
                  {loading ? (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Lütfen bekleyin...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : (
                    <>
                      <i className='bi bi-search me-2'></i>
                      Filtrele
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>

    {/* Card Body - Table */}
    <div className='card-body pt-0'>
      <CarStatusTable
        data={cars}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageSizeChange={(pageSize) => handlePageSizeChange(pageSize)}
        onPageChange={handlePageChange}
        onEdit={(r) => console.log('go to edit page')}
        onDelete={(r) => console.log('delete reservation')}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        loading={loading}
      />
    </div>
  </div>

  <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
</>
  )
}
